import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  CheckCircle2,
  Circle,
  Clock,
  Sparkles,
  ArrowLeft,
  Building2,
  Map,
  History,
} from 'lucide-react';

const changelog = [
  {
    version: '0.5.0',
    date: 'April 2026',
    title: 'Passwort-Reset & Belege im PDF',
    items: [
      'Mieter können Passwort selbst zurücksetzen – Self-Service E-Mail-Link (60 Min gültig)',
      '„Passwort vergessen?"-Link auf der Mieterportal-Login-Seite',
      'Belege werden automatisch in das finale Abrechnungs-PDF eingebettet',
      'PDF-Belege als zusätzliche Seiten angehängt (pypdf-Merge), Bild-Belege (JPG/PNG) als eigene Seiten',
      'Anhangs-Übersicht direkt nach der Zusammenfassung der Abrechnung',
      'PDF-Generator refaktoriert – ein gemeinsamer Helper für Vermieter-, Mieter- und E-Mail-Variante',
    ],
  },
  {
    version: '0.4.0',
    date: 'März 2026',
    title: 'E-Mail-Versand & Belege',
    items: [
      'Welcome-E-Mail an Mieter beim Anlegen eines Portal-Logins (Resend)',
      'Manueller E-Mail-Versand finalisierter Abrechnungen an Mieter inkl. PDF-Anhang',
      'Beleg-Upload für Kostenpositionen (PDF, JPG, PNG, bis 10 MB) via Object Storage',
      'Mehrere Belege pro Kosteneintrag, mit Vorschau und Download',
      'Status "Zuletzt versendet am ..." in der Abrechnungs-Detailansicht',
    ],
  },
  {
    version: '0.3.0',
    date: 'Februar 2026',
    title: 'Schadensmanagement & Mieterportal',
    items: [
      'Schadensmanagement: Schäden erfassen, priorisieren, Reparaturkosten dokumentieren',
      'Mieterportal mit eigenem Login (separater JWT-Bereich)',
      'Mieter können eigene Abrechnungen einsehen und finalisierte PDFs herunterladen',
      'Mieter können Schäden direkt online melden',
      'Vermieter verwaltet Mieterzugänge zentral unter „Mieterzugänge"',
      'Privilege Separation: Mieter-Tokens werden für Vermieter-Routen mit 403 abgelehnt',
    ],
  },
  {
    version: '0.2.0',
    date: 'Januar 2026',
    title: 'Kivera Rebranding & Landing',
    items: [
      'Rebranding zu „Kivera — Immobilienverwaltung, die mitdenkt."',
      'Öffentliche Landing Page mit Pricing (3 Tarife)',
      'Invite-only Registrierung (Einladungscodes)',
      'Vermieter als eigene Entität, an Einheit gekoppelt',
      'Bugfix: Nebenkostenvorauszahlungen werden korrekt über alle Monate des Zeitraums kumuliert',
    ],
  },
  {
    version: '0.1.0',
    date: 'Dezember 2025',
    title: 'MVP Release & Projektstart',
    items: [
      'Projektstart Kivera – initiale Architektur (React + FastAPI + MongoDB)',
      'JWT Authentifizierung (Register / Login)',
      'CRUD für Immobilien, Einheiten, Mieter, Kosten',
      '16 Kostenarten nach BetrKV',
      '4 Verteilerschlüssel: nach Wohnfläche, Personenzahl, Einheiten und verbrauchsabhängig (Zählerwerte pro Mieter)',
      'Automatische Abrechnungsberechnung',
      'PDF-Export der Nebenkostenabrechnung (ReportLab)',
      'Dashboard mit Kennzahlen',
    ],
  },
];

const roadmapSections = [
  {
    title: 'In Arbeit',
    icon: Clock,
    accent: 'bg-amber-100 text-amber-700',
    badge: 'In Arbeit',
    badgeClass: 'bg-amber-100 text-amber-700 hover:bg-amber-100',
    items: [
      {
        title: 'Eigene Absender-Domain pro Vermieter',
        desc: 'Statt der Standard-Adresse noreply@kivera.de können Vermieter ihre eigene verifizierte Domain hinterlegen – Mieter erhalten Mails dann z. B. von noreply@ihre-hausverwaltung.de.',
      },
    ],
  },
  {
    title: 'Geplant',
    icon: Circle,
    accent: 'bg-blue-100 text-blue-700',
    badge: 'Geplant',
    badgeClass: 'bg-blue-100 text-blue-700 hover:bg-blue-100',
    items: [
      {
        title: 'CSV / Excel Import',
        desc: 'Stammdaten und Kostenpositionen per CSV importieren – ideal beim Umzug aus Excel.',
      },
      {
        title: 'Mehrbenutzer-Rollen (Admin, Sachbearbeiter)',
        desc: 'Team-Funktionen für Hausverwaltungen mit feingranularen Berechtigungen.',
      },
      {
        title: 'DATEV-Export',
        desc: 'Buchungsstapel-Export für die Steuerberater-Software.',
      },
      {
        title: 'KI-Dokumentenerkennung',
        desc: 'Rechnungen fotografieren – Kategorie, Betrag und Datum werden automatisch erkannt.',
      },
      {
        title: 'Server-Refactoring & Production Hardening',
        desc: 'Router-Module splitten, vollständige Test-Coverage, Deployment-Vorbereitung.',
      },
      {
        title: 'Mobile-optimiertes Mieterportal',
        desc: 'Touch-optimierte Ansicht für Schadensmeldung und Abrechnungseinsicht.',
      },
    ],
  },
  {
    title: 'Backlog',
    icon: Sparkles,
    accent: 'bg-slate-100 text-slate-700',
    badge: 'Backlog',
    badgeClass: 'bg-slate-100 text-slate-700 hover:bg-slate-100',
    items: [
      {
        title: 'Banking-API für Mietzahlungsabgleich',
        desc: 'Automatischer Abgleich von Mieteingängen mit dem Mieterkonto.',
      },
      {
        title: 'Native Mobile App (iOS / Android)',
        desc: 'Schäden vor Ort mit Fotos erfassen, Push-Benachrichtigungen für Mieter.',
      },
    ],
  },
];

function RoadmapContent() {
  return (
    <div className="space-y-6">
      {roadmapSections.map((section) => {
        const Icon = section.icon;
        return (
          <Card key={section.title} className="border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${section.accent}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <h3
                  className="text-xl font-bold text-slate-900"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  {section.title}
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {section.items.map((item) => (
                  <div
                    key={item.title}
                    data-testid={`roadmap-item-${item.title.toLowerCase().replace(/\s+/g, '-').slice(0, 30)}`}
                    className="p-4 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <h4 className="font-medium text-slate-900">{item.title}</h4>
                      <Badge className={section.badgeClass}>{section.badge}</Badge>
                    </div>
                    <p className="text-sm text-slate-600">{item.desc}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function ChangelogContent() {
  return (
    <div className="space-y-6">
      {changelog.map((release, idx) => (
        <Card
          key={release.version}
          className="border-slate-200"
          data-testid={`changelog-${release.version}`}
        >
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
              <div className="flex items-center gap-3">
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    idx === 0
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  <CheckCircle2 className="h-5 w-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3
                      className="text-xl font-bold text-slate-900"
                      style={{ fontFamily: 'Manrope, sans-serif' }}
                    >
                      v{release.version}
                    </h3>
                    {idx === 0 && (
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                        Aktuell
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-slate-500">{release.date}</p>
                </div>
              </div>
              <p className="text-slate-700 font-medium">{release.title}</p>
            </div>
            <ul className="space-y-2">
              {release.items.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-slate-700">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function Header({ inApp }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm text-slate-500">
        {inApp ? (
          <Link to="/dashboard" className="hover:text-slate-900 flex items-center gap-1">
            <ArrowLeft className="h-3 w-3" /> Dashboard
          </Link>
        ) : (
          <Link to="/" className="hover:text-slate-900 flex items-center gap-1">
            <ArrowLeft className="h-3 w-3" /> Startseite
          </Link>
        )}
      </div>
      <h1
        className="text-4xl sm:text-5xl font-bold text-slate-900"
        style={{ fontFamily: 'Manrope, sans-serif' }}
      >
        Roadmap & Changelog
      </h1>
      <p className="text-slate-600 max-w-2xl">
        Transparenz, was wir gerade bauen und was bereits released ist – mit Fokus auf rechtssichere,
        zeitsparende Immobilienverwaltung.
      </p>
    </div>
  );
}

function Body() {
  return (
    <Tabs defaultValue="roadmap" className="w-full">
      <TabsList className="grid w-full grid-cols-2 max-w-md">
        <TabsTrigger value="roadmap" data-testid="tab-roadmap">
          <Map className="h-4 w-4 mr-2" />
          Roadmap
        </TabsTrigger>
        <TabsTrigger value="changelog" data-testid="tab-changelog">
          <History className="h-4 w-4 mr-2" />
          Changelog
        </TabsTrigger>
      </TabsList>
      <TabsContent value="roadmap" className="mt-6">
        <RoadmapContent />
      </TabsContent>
      <TabsContent value="changelog" className="mt-6">
        <ChangelogContent />
      </TabsContent>
    </Tabs>
  );
}

export default function Roadmap() {
  const { user } = useAuth();

  // In-App version (with sidebar) for logged-in landlords
  if (user) {
    return (
      <Layout>
        <div data-testid="roadmap-page" className="space-y-8">
          <Header inApp />
          <Body />
        </div>
      </Layout>
    );
  }

  // Public version with simple nav
  return (
    <div data-testid="roadmap-page" className="min-h-screen bg-slate-50">
      {/* Simple top nav */}
      <nav className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Building2 className="h-7 w-7 text-emerald-600" />
            <span
              className="font-bold text-lg text-slate-900"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Kivera
            </span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button variant="ghost" size="sm">Anmelden</Button>
            </Link>
            <Link to="/register">
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                Registrieren
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-12 space-y-8">
        <Header />
        <Body />
      </main>
    </div>
  );
}
